(ns superadmin.roles.create.view
  (:require ["@mui/material/Box$default" :as Box]
            ["@mui/material/Button$default" :as Button]
            ["@mui/material/Checkbox$default" :as Checkbox]
            ["@mui/material/FormControlLabel$default" :as FormControlLabel]
            ["@mui/material/FormGroup$default" :as FormGroup]
            ["@mui/material/Stack$default" :as Stack]
            ["@mui/material/ToggleButton$default" :as ToggleButton]
            ["@mui/material/ToggleButtonGroup$default" :as ToggleButtonGroup]
            ["@mui/material/Typography$default" :as Typography]
            [common.ocp.view :refer [defocp ocp]]
            [common.queries :refer [use-mutation]]
            [common.widgets.form-field :refer [form-field]]
            [common.widgets.input :refer [input]]
            [superadmin.roles.data :refer [apply-permission-dependencies permission-resources]]
            [superadmin.roles.queries :as query]
            [superadmin.roles.ui :refer [permission-chip]]
            [uix.core :as uix :refer [$ defui]]))

(defui permissions-editor
  [{:keys [form-data set-form-data]}]
  (let [is-permission-checked (fn [resource-id action-id]
                                (= "allowed"
                                   (get-in form-data
                                           [:permissions (keyword resource-id) (keyword action-id)])))
        toggle-permission (fn [resource-id action-id]
                            (fn [_]
                              (set-form-data
                               (fn [prev]
                                 (let [permissions (:permissions prev)
                                       checked (= "allowed" (get-in permissions [(keyword resource-id) (keyword action-id)]))
                                       updated-permissions (apply-permission-dependencies
                                                            permissions
                                                            resource-id
                                                            action-id
                                                            (not checked))]
                                   (assoc prev :permissions updated-permissions))))))]

    ($ Stack {:spacing 3}
       ($ Typography {:variant "h6"} "Permissions")
       (for [resource permission-resources]
         ($ Box {:key (:id resource)
                 :sx #js {:p 0
                          :border "0px solid"
                          :borderColor "divider"
                          :borderRadius 0
                          :mb 0}}
            ($ Stack {:spacing 2
                      :direction "row"}
               ($ Typography {:variant "subtitle1"
                              :sx #js {:width 120
                                       :fontWeight 400}}
                  (:label resource))
               ($ FormGroup {:row true :sx #js {:gap 1}}
                  (for [action (:actions resource)]
                    ($ FormControlLabel
                       {:key (str (:id resource) "-" (:id action))
                        :control ($ permission-chip
                                    {:label (:label action)
                                     :selected (is-permission-checked (:id resource) (:id action))
                                     :onToggle (toggle-permission (:id resource) (:id action))})
                        :sx #js {:mr 0.2
                                 :width 110}})))))))))

(defui ocp-footer [{:keys [is-valid? is-edit? is-submitting]}]
  ($ Button
     {:type "submit"
      :variant "contained"
      :disabled (or (not is-valid?) is-submitting)
      :data-testid (if is-edit? "ocp-update-role-button" "ocp-create-role-button")}
     (if is-edit? "Update Role" "Create Role")))

(defocp role-create-edit-ocp :role/create-edit
  {:width "50%"}
  [props]
  (let [{:keys [role]} props
        is-edit? (some? (:id role))
        role-id (:id role)
        [form-data set-form-data] (uix/use-state
                                   (or role
                                       {:name ""
                                        :allowed-scopes []
                                        :permissions {}}))
        role-name (:name form-data)
        {:keys [allowed-scopes superadmin-scope permissions]} form-data
        is-valid? (every? identity [(not-empty role-name)
                                    (or (not-empty allowed-scopes) superadmin-scope)
                                    (not-empty permissions)])
        create-mutation (use-mutation (query/create-role-mutation))
        update-mutation (use-mutation (query/update-role-mutation {:role-id (or role-id 0)}))

        modify-super-admin-scope #(cond-> %
                                    superadmin-scope (->
                                                      (dissoc :superadmin-scope)
                                                      (assoc :allowed-scopes [])))

        handle-submit (fn []
                        (let [mutation (if is-edit? update-mutation create-mutation)]
                          ((:mutate mutation) (modify-super-admin-scope form-data))))
        is-submitting (or (:isPending create-mutation)
                          (when is-edit? (:isPending update-mutation)))]

    ($ ocp
       {:title (if is-edit? "Edit Role" "Create Role")
        :on-submit handle-submit
        :footer ($ ocp-footer {:is-edit? is-edit?
                               :is-valid? is-valid?
                               :is-submitting is-submitting})}
       ($ Stack {:spacing 3}

          ;; Name field
          ($ form-field
             {:label "Role Name"
              :required? true}
             ($ input
                {:value (:name form-data)
                 :onChange #(set-form-data
                             (fn [prev]
                               (assoc prev :name (-> % .-target .-value))))
                 :placeholder "Enter role name"
                 :helperText "Unique name for this role (max 100 characters)"
                 :fullWidth true}))

          ;; Allowed Scopes
          ($ Stack {:spacing 3}
             ($ Typography {:variant "h6"} "Allowed Scopes")
             ($ Stack
                {:direction "row"
                 :gap 2}
                ($ ToggleButtonGroup
                   {:value (if (:superadmin-scope form-data) "" (first (:allowed-scopes form-data)))
                    :disabled (:superadmin-scope form-data)
                    :exclusive true
                    :onChange (fn [_event new-value]
                                (set-form-data
                                 (fn [prev]
                                   (assoc prev :allowed-scopes [new-value]))))
                    :size "small"}
                   ($ ToggleButton {:value "agency"} "Agency")
                   ($ ToggleButton {:value "advertiser"} "Advertiser"))
                ($ FormControlLabel
                   {:control ($ Checkbox
                                {:checked (:superadmin-scope form-data)
                                 :onChange #(let [checked (-> % .-target .-checked)]
                                              (set-form-data
                                               (fn [prev]
                                                 (assoc prev :superadmin-scope checked))))})
                    :label "Superadmin?"})))

          ;; Permissions section
          ($ form-field
             ($ permissions-editor {:form-data form-data
                                    :set-form-data set-form-data}))))))
